import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Heart, LogIn, Store } from "lucide-react";
import api from "../../lib/api";
import ListPageLayout, { SkeletonGrid, EmptyState } from "../../components/smart/ListPageLayout";
import ResultCard from "../../components/smart/ResultCard";
import { useAuth } from "../../context/AuthContext";
import { useGeo, distanceKm, fmtDistance } from "../../lib/homeMode";
import { CATEGORY_LABELS, categoryKey } from "../../components/map/categoryIcons";
import { toast } from "sonner";

export default function FavoritesList() {
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const geo = useGeo();
  const [items, setItems] = useState(null);
  const [q, setQ] = useState("");

  const load = () => api.get("/favorites").then(({ data }) => setItems(data || [])).catch(() => setItems([]));
  useEffect(() => { if (user?.role === "customer") load(); }, [user]);

  const toggleFav = async (id) => {
    try { await api.post(`/favorites/business/${id}`); toast.success("Preferiti aggiornati"); load(); }
    catch { toast.error("Impossibile aggiornare i preferiti"); }
  };

  const shown = useMemo(() => {
    if (!items) return null;
    let list = items.slice();
    if (q.trim()) { const s = q.toLowerCase(); list = list.filter((p) => (p.name || "").toLowerCase().includes(s)); }
    return list.map((p) => ({ ...p, _d: geo ? distanceKm(geo, { lat: p.lat, lng: p.lng }) : null }));
  }, [items, q, geo]);

  if (loading) {
    return (
      <ListPageLayout title="Preferiti" subtitle="Le tue attività salvate" icon={Heart}>
        <SkeletonGrid count={4} />
      </ListPageLayout>
    );
  }

  if (!user) {
    return (
      <ListPageLayout title="Preferiti" subtitle="Le tue attività salvate" icon={Heart}>
        <div className="py-20 text-center border border-white/10 rounded-2xl bg-[#131316]" data-testid="fav-login-required">
          <Heart className="h-10 w-10 mx-auto text-white/20 mb-3" />
          <p className="text-white/60 text-sm mb-4">Accedi per vedere e salvare i tuoi preferiti.</p>
          <button data-testid="fav-login-btn" onClick={() => navigate("/login")} className="inline-flex items-center gap-2 rounded-full bg-[#00E676] text-black font-semibold px-5 py-2.5">
            <LogIn className="h-4 w-4" /> Accedi
          </button>
        </div>
      </ListPageLayout>
    );
  }

  return (
    <ListPageLayout title="Preferiti" subtitle="Le attività che hai salvato" icon={Heart} search={q} onSearch={setQ} searchPlaceholder="Cerca nei preferiti…">
      {shown === null ? (
        <SkeletonGrid count={4} />
      ) : shown.length === 0 ? (
        <EmptyState icon={Heart} message="Non hai ancora salvato attività. Tocca il cuore su un'attività per aggiungerla." />
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6" data-testid="favorites-list">
          {shown.map((p) => (
            <ResultCard
              key={p.id}
              testid={`fav-card-${p.id}`}
              image={p.image}
              fallbackIcon={Store}
              title={p.name}
              subtitle={`${CATEGORY_LABELS[categoryKey(p.category)] || p.category}${p.zone ? ` · ${p.zone}` : ""}`}
              statusLabel={p.orderable ? "Aperto" : "In arrivo"}
              statusType={p.orderable ? "open" : "arriving"}
              rating={p.rating}
              distance={fmtDistance(p._d)}
              isFavorite
              onFavorite={() => toggleFav(p.id)}
              onOpen={() => navigate(`/attivita/${p.id}`)}
              onMap={() => navigate("/esplora")}
            />
          ))}
        </div>
      )}
    </ListPageLayout>
  );
}
